import { mediaUrl } from '../../../lib/media-url.js';
import { isStripeCheckout } from '../../../lib/checkout-mode.js';
import { useState } from 'react';
import { ShoppingCart, Check, Send, Package, Plus, Minus } from 'lucide-react';
import { Button } from '../../../components/ui/Button.jsx';
import { cn, formatCurrency } from '../../../lib/utils.js';
import { displayUnitCents, priceDisplayMode, vatRateOf } from '../../../lib/price.js';
import { useCart } from '../../../store/cart.jsx';

export function CompactRowProductCard({
  product,
  tenant,
  onAddToCart,
  onViewProduct,
  onInquiry,
  index = 0,
  style,
  className,
  ...props
}) {
  const [quantity, setQuantity] = useState(1);
  const [justAdded, setJustAdded] = useState(false);

  let isInCartFn = () => false;
  let addItemFn = () => {};
  try {
    const cart = useCart();
    if (cart) {
      isInCartFn = cart.isInCart;
      addItemFn = cart.addItem;
    }
  } catch {}

  const sku = product?.sku || product?.id || '';
  const alreadyInCart = sku ? isInCartFn(sku) : false;

  let images = [];
  try {
    images = Array.isArray(product?.images)
      ? product.images
      : (product?.images_json ? JSON.parse(product.images_json) : []);
  } catch {
    images = [];
  }
  const primaryImage = (images && images[0]?.url) || (typeof images[0] === 'string' && images[0]) || product?.primaryImageUrl || product?.image_url || '';

  const unitPriceCents = product?.price?.cents !== undefined
    ? Number(product.price.cents)
    : (product?.base_price_cents !== undefined
      ? Number(product.base_price_cents)
      : (product?.base_price !== undefined ? Number(product.base_price) : (Number(product?.unitPriceCents) || 0)));

  const isBuyable = unitPriceCents > 0;
  // a-055b: product.price.formatted (Serverwert) bewusst ignoriert; Betrag aus price.cents, siehe src/lib/price.js
  const priceMode = priceDisplayMode(tenant);
  const formattedPrice = formatCurrency(displayUnitCents(unitPriceCents, priceMode, vatRateOf(tenant)));

  const handleCardClick = () => {
    if (onViewProduct) {
      onViewProduct(product?.slug || product?.sku || product?.id);
    }
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (onAddToCart) {
      onAddToCart(product, quantity);
    } else {
      addItemFn(product, quantity);
    }
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1500);
  };

  const staggerDelay = `${Math.min(Math.max(0, index) * 20, 200)}ms`;

  return (
    <div
      data-card-variant="compact_row"
      style={{
        ...style,
        animationDelay: staggerDelay,
        '--stagger-delay': staggerDelay,
      }}
      onClick={handleCardClick}
      className={cn(
        "group relative flex min-h-[72px] w-full items-center justify-between gap-3 sm:gap-4 rounded-xl border border-[var(--border)] bg-white p-2.5 sm:p-3 transition-all hover:border-[var(--primary)] hover:bg-[var(--surface-container-low,#f8fafc)]/50 hover:shadow-sm cursor-pointer anim-card-stagger",
        className
      )}
      {...props}
    >
      {/* Left: Thumbnail & Manufacturer / SKU */}
      <div className="flex items-center gap-3 min-w-0 flex-1">
        
        {/* 56x56px Image Preview */}
        <div className="relative flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface-container-low,#f8fafc)]">
          {primaryImage ? (
            <img
              src={mediaUrl(primaryImage)}
              alt={product?.name || 'Produkt'}
              loading="lazy"
              className="h-full w-full object-contain p-1 transition-transform group-hover:scale-105"
            />
          ) : (
            <Package className="h-6 w-6 text-[var(--secondary)]/40" />
          )}
        </div>

        {/* Info Column */}
        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="flex items-center gap-2 text-xs font-semibold">
            <span className="font-extrabold uppercase tracking-wider text-[var(--primary,#e3000b)] truncate max-w-[120px]">
              {product?.manufacturer || 'Fachhandel'}
            </span>
            <span className="font-mono text-xs text-[var(--secondary)] bg-[var(--surface-container-low,#f8fafc)] px-1 rounded border border-[var(--border)]">
              {sku}
            </span>
          </div>

          <h4 className="font-display text-xs sm:text-sm font-bold text-[var(--foreground)] group-hover:text-[var(--primary)] transition-colors truncate">
            {product?.name}
          </h4>

          {product?.category && (
            <p className="text-xs text-[var(--secondary)] truncate hidden md:block">
              {product.category}
            </p>
          )}
        </div>
      </div>

      {/* Right: Pricing, Quantity Stepper & Add Button */}
      <div className="flex items-center gap-3 shrink-0" onClick={(e) => e.stopPropagation()}>
        
        {/* Price */}
        <div className="text-right hidden xs:block">
          <span className="text-xs font-bold uppercase tracking-widest text-[var(--secondary)] block">
            {isBuyable ? (priceMode === 'gross' ? 'Brutto' : 'Netto') : 'Status'}
          </span>
          <span className="font-display text-xs sm:text-sm font-extrabold text-[var(--foreground)]">
            {isBuyable ? formattedPrice : 'Auf Anfrage'}
          </span>
        </div>

        {isBuyable ? (
          <div className="flex items-center gap-1.5">
            {/* Quantity Stepper */}
            <div className="hidden sm:flex items-center rounded-lg border border-[var(--border)] bg-white shadow-2xs">
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                className="flex h-7 w-7 items-center justify-center text-[var(--secondary)] hover:bg-[var(--surface-container-low)] rounded-l-lg transition-colors cursor-pointer"
                aria-label="Menge verringern"
              >
                <Minus className="h-3 w-3" />
              </button>
              <span className="w-7 text-center font-mono text-xs font-bold text-[var(--foreground)] select-none">
                {quantity}
              </span>
              <button
                type="button"
                onClick={() => setQuantity((q) => q + 1)}
                className="flex h-7 w-7 items-center justify-center text-[var(--secondary)] hover:bg-[var(--surface-container-low)] rounded-r-lg transition-colors cursor-pointer"
                aria-label="Menge erhöhen"
              >
                <Plus className="h-3 w-3" />
              </button>
            </div>

            {/* 1-Click Add Button */}
            <Button
              size="sm"
              variant={justAdded || alreadyInCart ? 'secondary' : 'default'}
              className="h-8 px-2.5 sm:px-3 text-xs font-bold gap-1 rounded-lg shadow-2xs cursor-pointer"
              onClick={handleAddToCart}
              title={isStripeCheckout(tenant) ? 'In den Warenkorb hinzufügen' : 'In den Anfragekorb'}
            >
              {justAdded ? (
                <>
                  <Check className="h-3.5 w-3.5 text-emerald-600" />
                  <span className="hidden md:inline">+ {quantity}</span>
                </>
              ) : (
                <>
                  <ShoppingCart className="h-3.5 w-3.5" />
                  <span className="hidden md:inline">+ {quantity}</span>
                </>
              )}
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="h-8 px-2.5 text-xs font-medium gap-1 rounded-lg cursor-pointer"
            onClick={() => {
              if (onInquiry) onInquiry(product);
              else handleCardClick();
            }}
            title="Projektangebot anfragen"
          >
            <Send className="h-3 w-3" />
            <span className="hidden sm:inline">Anfrage</span>
          </Button>
        )}
      </div>
    </div>
  );
}
